/**
 * Compact monthly AI budget meter for the nav rail footer.
 * Links to Settings → Usage.
 */
import { NavLink } from "react-router";
import { budgetUsedPct, useAiBudget } from "@/hooks/useAiBudget";
import { useNavRailTip } from "./NavRailTip";

type Props = {
  open: boolean;
};

export default function NavAiBudgetBar({ open }: Props) {
  const { data } = useAiBudget();
  const { bindTip, tipPortal } = useNavRailTip(!open);

  if (!data) return null;

  const pct = budgetUsedPct(data);
  // Library 01 AI usage: whole percent ("64%"); under 1 reads "<1%" so a
  // little spend never shows as none.
  const pctLabel = pct > 0 && pct < 1 ? "<1%" : `${Math.round(pct)}%`;
  // Used is co-text2 at rest; past 80% and exhausted take the alert ink (no orange in the kit).
  const fill = data.exhausted || pct > 80 ? "var(--co-alert-ink)" : "var(--co-text2)";
  const label = data.exhausted
    ? "AI budget exhausted"
    : `${pctLabel} of AI budget used`;
  const tipText = data.exhausted ? "Usage exhausted" : `Usage · ${pctLabel}`;

  const size = 18;
  const stroke = 2;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - pct / 100);

  /* The ring is the footer's glyph, at 6 on a 30 row, folded or open.
     Opening adds the word and the percent to its right. */
  return (
    <>
      <NavLink
        to="settings?page=usage"
        className="unstyled-link"
        aria-label={label}
        {...(open ? {} : bindTip(tipText))}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 6px",
          width: open ? "100%" : 30,
          height: 30,
          flex: "none",
          position: "relative",
          color: "inherit",
        }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden style={{ flex: "none" }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--co-faint)" strokeWidth={stroke} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={fill}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        {open ? (
          <span className="co-rail-text" style={{ display: "flex", minWidth: 0, flex: 1, alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontWeight: 700,
                fontSize: 10,
                lineHeight: "13px",
                letterSpacing: "1.2px",
                textTransform: "uppercase",
                color: data.exhausted ? "var(--co-alert-ink)" : "var(--co-faint)",
                whiteSpace: "nowrap",
              }}
            >
              {data.exhausted ? "Exhausted" : "Usage"}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, lineHeight: "13px", letterSpacing: "0.4px", color: "var(--co-muted)", whiteSpace: "nowrap" }}>
              {pctLabel}
            </span>
          </span>
        ) : null}
      </NavLink>
      {tipPortal}
    </>
  );
}
